import { yupResolver } from '@hookform/resolvers/yup';
import EventBanner, {
  BackgroundFilter,
  EventBannerFragment,
} from '-/Event/EventBanner';
import { Link, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import {
  type Client,
  useClient,
  useMutation,
  useQuery,
  useSubscription,
} from 'urql';
import * as Yup from 'yup';
import { EventClockFragment } from '@/components/Event/EventClock';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import FormContentBoundary from '@/components/UI/FormContentBoundary';
import Text from '@/components/UI/Text';
import { graphql, readFragment } from '@/gql/graphql';
import eventStatus from '@/utils/eventStatus';
import type { EventScorekeepingPanelFormValues } from '../EventScorekeeping.types';
import { buildScorekeepingAssignmentMutationVariables } from './EventScorekeepingPanel.utils';
import EventScorekeepingPanelBadge, {
  EventScorekeepingPanelBadgeFragment,
} from './EventScorekeepingPanelBadge';
import EventScorekeepingPanelPreview from './EventScorekeepingPanelPreview';
import { EventScorekeepingPanelPreviewRecipientItemFragment } from './EventScorekeepingPanelPreview/EventScorekeepingPanelPreviewRecipientItem';
import { EventScorekeepingPanelPreviewTimeFragment } from './EventScorekeepingPanelPreview/EventScorekeepingPanelPreviewTime';
import EventScorekeepingPanelTeam, {
  EventScorekeepingPanelTeamFragment,
} from './EventScorekeepingPanelTeam';
import { EventScorekeepingPanelTeamItemFragment } from './EventScorekeepingPanelTeam/EventScorekeepingPanelTeamItem';
import EventScorekeepingPanelTimer, {
  EventScorekeepingPanelTimerFragment,
} from './EventScorekeepingPanelTimer';

const EndGame = graphql(/* GraphQL */ `
  mutation EndGame($eventId: uuid!) {
    update_event_by_pk(
      pk_columns: { id: $eventId }
      _set: { status: completed }
    ) {
      id
      status
      event_clock {
        event_id
      }
    }
  }
`);
const UpsertAssignment = graphql(/* GraphQL */ `
  mutation UpsertAssignment(
    $badgeAssignees: [badge_assignee_insert_input!]!
    $deletedAssigneeIds: [uuid!]
  ) {
    delete_badge_assignee(where: { id: { _in: $deletedAssigneeIds } }) {
      affected_rows
    }
    insert_badge_assignee(
      objects: $badgeAssignees
      on_conflict: {
        constraint: badge_assignee_pkey
        update_columns: [order, parent_id, elapsed_segment_ms, event_segment_id]
      }
    ) {
      affected_rows
    }
  }
`);
const GetEventScorekeepingPanelFormAssignee = graphql(/* GraphQL */ `
  query GetEventScorekeepingPanelFormAssignee($badgeAssigneeId: uuid!) {
    badge_assignee_by_pk(id: $badgeAssigneeId) {
      id
      elapsed_segment_ms
      event_segment {
        id
        title
        duration
        duration_offset
        is_duration_capped
      }
      badge_assignees(order_by: { order: asc_nulls_first }) {
        ...EventScorekeepingPanelPreviewRecipientItem
      }
    }
  }
`, [EventScorekeepingPanelPreviewRecipientItemFragment]);
const GetEventScorekeepingPanelFormEvent = graphql(/* GraphQL */ `
  query GetEventScorekeepingPanelFormEvent($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      event_clock {
        event_id
        elapsed_segment_ms
        event_segment {
          id
          duration
          duration_offset
          title
          is_duration_capped
        }
      }
      ...EventScorekeepingPanelTeam
    }
  }
`, [EventScorekeepingPanelTeamFragment]);
const GetEventScorekeepingPanel = graphql(/* GraphQL */ `
  query GetEventScorekeepingPanel($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      status
      permissions
      event_segment_group {
        id
        is_timed
      }
      event_clock {
        event_id
      }
      ...EventScorekeepingPanelBadge
      ...EventScorekeepingPanelTeam
      ...EventBanner
      ...EventClock
      ...EventScorekeepingPanelTimer
      ...EventScorekeepingPanelPreviewTime
    }
  }
`, [
  EventScorekeepingPanelTeamFragment,
  EventScorekeepingPanelBadgeFragment,
  EventBannerFragment,
  EventClockFragment,
  EventScorekeepingPanelTimerFragment,
  EventScorekeepingPanelPreviewTimeFragment,
]);
const WatchEventScorekeepingPanel = graphql(/* GraphQL */ `
  subscription WatchEventScorekeepingPanel($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      status
      ...EventBanner
      ...EventClock
      ...EventScorekeepingPanelTimer
    }
  }
`, [
  EventBannerFragment,
  EventClockFragment,
  EventScorekeepingPanelTimerFragment,
]);
const ValidationSchema: Yup.ObjectSchema<EventScorekeepingPanelFormValues> =
  Yup.object({
    isEditing: Yup.boolean().required(),
    selectedTeam: Yup.object({
      id: Yup.string().required(),
      title: Yup.string().required(),
      home_primary_color: Yup.string().nullable().defined(),
      avatar_id: Yup.string().nullable().defined(),
    }).required(),
    selectedPerson: Yup.object({
      jersey_number: Yup.string().nullable().defined(),
      person_team: Yup.object({
        person_id: Yup.string().required(),
        team_id: Yup.string().required(),
        jersey_number: Yup.string().nullable().defined(),
        person: Yup.object({
          id: Yup.string().required(),
          name: Yup.string().nullable().defined(),
          avatar_id: Yup.string().nullable().defined(),
        })
          .nullable()
          .defined(),
        team: Yup.object({
          id: Yup.string().required(),
          home_primary_color: Yup.string().nullable().defined(),
        })
          .nullable()
          .defined(),
      })
        .nullable()
        .defined(),
      id: Yup.string().required(),
      name: Yup.string().nullable().defined(),
      avatar_id: Yup.string().nullable().defined(),
      color: Yup.string().nullable().defined(),
    })
      .nullable()
      .defined(),
    selectedBadge: Yup.object({
      id: Yup.string().required(),
      title: Yup.string().required(),
      color: Yup.string().required(),
      can_quick_assign: Yup.boolean().nullable().defined(),
      attribute_score: Yup.object({
        id: Yup.string().required(),
        title: Yup.string().required(),
        default_numeric_value: Yup.number().nullable().defined(),
      })
        .nullable()
        .defined(),
      badge_attributes: Yup.array()
        .of(
          Yup.object({
            id: Yup.string().required(),
            title: Yup.string().required(),
            options: Yup.array()
              .of(Yup.string().required())
              .nullable()
              .defined(),
            is_required: Yup.boolean().defined(),
            attribute_type: Yup.string().oneOf(['numeric', 'text']).required(),
            default_value: Yup.string().nullable().defined(),
          }).required(),
        )
        .required(),
    })
      .nullable()
      .defined(),
    selectedRecipientId: Yup.string().nullable().defined(),
    selectedTime: Yup.object({
      elapsed_segment_ms: Yup.number().required(),
      event_segment: Yup.object({
        id: Yup.string().required(),
        title: Yup.string().required(),
        duration: Yup.number().required(),
        duration_offset: Yup.number().nullable().defined(),
      })
        .nullable()
        .defined(),
    })
      .nullable()
      .defined(),
    recipients: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().required(),
          isDeleted: Yup.boolean().required(),
          isNew: Yup.boolean().required(),
          person_team: Yup.object({
            person_id: Yup.string().required(),
            team_id: Yup.string().required(),
            jersey_number: Yup.string().nullable().defined(),
            person: Yup.object({
              id: Yup.string().required(),
              name: Yup.string().nullable().defined(),
              avatar_id: Yup.string().nullable().defined(),
            }).required(),
            team: Yup.object({
              id: Yup.string().required(),
              home_primary_color: Yup.string().nullable().defined(),
            }).required(),
          }).required(),
          badge_assignee_attributes: Yup.array()
            .of(
              Yup.object({
                id: Yup.string().required(),
                value: Yup.string().nullable().defined(),
                badge_attribute: Yup.object({
                  id: Yup.string().required(),
                  attribute_type: Yup.string()
                    .oneOf(['numeric', 'text'])
                    .required(),
                }).required(),
              }).required(),
            )
            .required(),
          badge: Yup.object({
            id: Yup.string().required(),
            title: Yup.string().required(),
            color: Yup.string().required(),
            can_quick_assign: Yup.boolean().nullable().defined(),
            attribute_score: Yup.object({
              id: Yup.string().required(),
              title: Yup.string().required(),
              default_numeric_value: Yup.number().nullable().defined(),
            })
              .nullable()
              .defined(),
            badge_attributes: Yup.array()
              .of(
                Yup.object({
                  id: Yup.string().required(),
                  title: Yup.string().required(),
                  options: Yup.array()
                    .of(Yup.string().required())
                    .nullable()
                    .defined(),
                  is_required: Yup.boolean().defined(),
                  attribute_type: Yup.string()
                    .oneOf(['numeric', 'text'])
                    .required(),
                  default_value: Yup.string().nullable().defined(),
                }).required(),
              )
              .required(),
          }).required(),
        }).required(),
      )
      .required('At least one recipient is required')
      .min(1, 'At least one recipient is required')
      .test('validate-attributes', function (recipients) {
        if (!recipients) return true;
        for (const [recipientIndex, recipient] of recipients.entries()) {
          if (!recipient.badge_assignee_attributes) continue;
          const badgeAttributesById = new Map(
            recipient.badge?.badge_attributes?.map((badgeAttribute) => [
              badgeAttribute.id,
              badgeAttribute,
            ]),
          );
          for (const [
            attrIndex,
            assigneeAttr,
          ] of recipient.badge_assignee_attributes.entries()) {
            const badgeAttribute = badgeAttributesById.get(
              assigneeAttr.badge_attribute.id,
            );
            if (
              badgeAttribute?.is_required &&
              (!assigneeAttr.value || assigneeAttr.value.trim() === '')
            ) {
              return this.createError({
                path: `recipients[${recipientIndex}].badge_assignee_attributes[${attrIndex}].value`,
                message: 'Required',
              });
            }
            if (
              assigneeAttr.value &&
              assigneeAttr.value.trim() !== '' &&
              assigneeAttr.badge_attribute.attribute_type === 'numeric'
            ) {
              const numValue = Number(assigneeAttr.value);
              if (Number.isNaN(numValue) || !Number.isFinite(numValue)) {
                return this.createError({
                  path: `recipients[${recipientIndex}].badge_assignee_attributes[${attrIndex}].value`,
                  message: `Non-Number not allowed: ${assigneeAttr.value}`,
                });
              }
            }
          }
        }
        return true;
      }),
  });

async function getEventScorekeepingPanelDefaultValues({
  client,
  eventId,
  badgeAssigneeId,
}: {
  client: Client;
  eventId: string;
  badgeAssigneeId?: string;
}) {
  const eventQuery = await client.query(GetEventScorekeepingPanelFormEvent, {
    eventId,
  });
  const eventTeamFragment = readFragment(
    EventScorekeepingPanelTeamFragment,
    eventQuery.data?.event_by_pk,
  );
  const firstTeam = readFragment(
    EventScorekeepingPanelTeamItemFragment,
    eventTeamFragment?.event_teams[0]?.team,
  );
  if (!badgeAssigneeId) {
    return {
      isEditing: false,
      selectedTeam: firstTeam || null,
      selectedPerson: null,
      selectedBadge: null,
      recipients: [],
      selectedRecipientId: null,
      selectedTime: null,
    };
  }
  const badgeAssigneeQuery = await client.query(
    GetEventScorekeepingPanelFormAssignee,
    {
      badgeAssigneeId,
    },
  );
  const badgeAssignees =
    badgeAssigneeQuery.data?.badge_assignee_by_pk?.badge_assignees?.map(
      (assignee) => {
        const data = readFragment(
          EventScorekeepingPanelPreviewRecipientItemFragment,
          assignee,
        );
        return {
          ...data,
          isDeleted: false,
          isNew: false,
        };
      },
    ) || [];
  const firstBadgeAssignee = badgeAssigneeQuery.data?.badge_assignee_by_pk;
  return {
    isEditing: false,
    selectedTeam: firstTeam || null,
    selectedPerson: null,
    selectedBadge: null,
    recipients: badgeAssignees,
    selectedRecipientId: null,
    selectedTime: firstBadgeAssignee?.event_segment
      ? {
          elapsed_segment_ms: firstBadgeAssignee?.elapsed_segment_ms ?? 0,
          event_segment: firstBadgeAssignee?.event_segment,
        }
      : null,
  };
}

export default function EventScorekeepingPanel({
  basePath,
}: {
  basePath: string;
}) {
  const client = useClient();
  const { setParams } = useRouter();
  const [, endGame] = useMutation(EndGame);
  const [, upsertAssignment] = useMutation(UpsertAssignment);
  const { eventId, badgeAssigneeId } = useLocalSearchParams<{
    eventId: string;
    badgeAssigneeId: string;
  }>();
  const form = useForm<EventScorekeepingPanelFormValues>({
    mode: 'onChange',
    resolver: yupResolver(ValidationSchema),
    defaultValues: async () => {
      const defaultValues = await getEventScorekeepingPanelDefaultValues({
        client,
        eventId,
        badgeAssigneeId,
      });
      return defaultValues;
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: tracking reset in deps will cause infinite loop
  useEffect(() => {
    if (!badgeAssigneeId) {
      form.reset(
        {
          selectedTeam: form.getValues('selectedTeam'),
          recipients: [],
          selectedPerson: null,
          selectedBadge: null,
          selectedRecipientId: null,
          selectedTime: null,
        },
        {
          keepDefaultValues: true,
        },
      );
    }
  }, [badgeAssigneeId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: tracking reset in deps will cause infinite loop
  useEffect(() => {
    (async () => {
      const newDefaultValues = await getEventScorekeepingPanelDefaultValues({
        client,
        eventId,
        badgeAssigneeId,
      });
      form.reset({
        ...newDefaultValues,
        selectedTeam: form.getValues('selectedTeam'),
      });
    })();
  }, [badgeAssigneeId, client, eventId]);
  const [{ data }] = useQuery({
    query: GetEventScorekeepingPanel,
    variables: {
      eventId,
    },
  });
  useSubscription({
    query: WatchEventScorekeepingPanel,
    variables: {
      eventId,
    },
  });
  const event = data?.event_by_pk;
  const onPressSave = async (values: EventScorekeepingPanelFormValues) => {
    const mutationVariables = buildScorekeepingAssignmentMutationVariables({
      eventId,
      values,
    });

    if (mutationVariables) {
      await upsertAssignment(mutationVariables);
      form.reset(
        {
          ...form.formState.defaultValues,
          selectedTeam: form.getValues('selectedTeam'),
        },
        {
          keepDefaultValues: true,
        },
      );
      if (badgeAssigneeId) {
        setParams({
          eventId,
          badgeAssigneeId: undefined,
        });
      }
    }
  };
  return (
    <FormProvider {...form}>
      <Box flex={3} style={style.container}>
        <FormContentBoundary loading={form.formState.isLoading}>
          <ScrollView contentContainerStyle={style.contentContainer}>
            <Box space="sm" flex>
              <EventBanner
                info={
                  <Box center>
                    <BackgroundFilter opacity={0.5} />
                    <Box px="xl" space="md" center>
                      {event?.permissions?.edit_score ? (
                        <Link
                          asChild
                          href={{
                            pathname: '/event/[eventId]/scorekeeping/score',
                            params: {
                              eventId,
                            },
                          }}
                        >
                          <ActionButton
                            variant="secondary"
                            forceLabel
                            label="Edit"
                            labelVariant="white"
                            icon="create-outline"
                            iconVariant="white"
                          />
                        </Link>
                      ) : null}

                      {event?.event_clock?.event_id ? (
                        <ActionButton
                          variant="error"
                          forceLabel
                          label="End Game"
                          labelVariant="white"
                          onPress={() => {
                            endGame({
                              eventId,
                            });
                          }}
                        />
                      ) : null}

                      {event?.status ? (
                        <Text variant="white" size="2xl">
                          {eventStatus[event.status]}
                        </Text>
                      ) : null}
                    </Box>
                  </Box>
                }
                fragment={event}
              />
            </Box>
            <Box space="md" py="md">
              {event?.status !== 'completed' ? (
                <EventScorekeepingPanelTimer fragment={event} />
              ) : null}
              <Card mx="md">
                <EventScorekeepingPanelBadge fragment={event} />
              </Card>
              <Card mx="md">
                <EventScorekeepingPanelTeam fragment={event} />
              </Card>
            </Box>
          </ScrollView>
          <EventScorekeepingPanelPreview
            basePath={basePath}
            timeFragment={event}
            onSubmit={form.handleSubmit(onPressSave)}
          />
        </FormContentBoundary>
      </Box>
    </FormProvider>
  );
}
const style = StyleSheet.create({
  container: {
    flex: 3,
  },
  contentContainer: {
    paddingBottom: 300,
  },
});
